import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * Toast notification component
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {string} props.type - The type of toast (success, error, warning, info)
 * @param {Function} props.onClose - Function to call when toast is closed
 * @param {number} props.autoClose - Time in ms before auto-closing (0 to disable)
 * @param {string} props.position - Position of the toast (top-right, top-left, bottom-right, bottom-left)
 */
const Toast = ({ 
  message, 
  type = 'success', 
  onClose, 
  autoClose = 5000,
  position = 'bottom-right'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    if (autoClose > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoClose);
      
      return () => clearTimeout(timer);
    }
  }, [autoClose]);
  
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose && onClose();
    }, 300); // Wait for animation to complete
  };
  
  const getIcon = () => {
    switch (type) {
      case 'success': return 'CheckCircle';
      case 'error': return 'AlertCircle';
      case 'warning': return 'AlertTriangle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };
  
  const getBackgroundColor = () => {
    switch (type) {
      case 'success': return 'bg-success';
      case 'error': return 'bg-error';
      case 'warning': return 'bg-warning';
      case 'info': return 'bg-info';
      default: return 'bg-primary';
    }
  };
  
  const getTextColor = () => {
    switch (type) {
      case 'success': return 'text-white';
      case 'error': return 'text-white';
      case 'warning': return 'text-white';
      case 'info': return 'text-white';
      default: return 'text-white';
    }
  };
  
  const getPositionClasses = () => {
    switch (position) {
      case 'top-right': return 'top-4 right-4';
      case 'top-left': return 'top-4 left-4';
      case 'bottom-left': return 'bottom-4 left-4';
      case 'bottom-right': 
      default: return 'bottom-4 right-4';
    }
  };
  
  return (
    <div 
      className={`fixed ${getPositionClasses()} z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div className={`${getBackgroundColor()} ${getTextColor()} rounded-lg shadow-lg flex items-center p-4 min-w-[300px] max-w-md`}>
        <div className="mr-3">
          <Icon name={getIcon()} size={20} />
        </div>
        <div className="flex-1 mr-2">
          {message}
        </div>
        <button 
          onClick={handleClose}
          className="text-white opacity-70 hover:opacity-100 transition-opacity"
        >
          <Icon name="X" size={16} />
        </button>
      </div>
    </div>
  );
};

export default Toast;
